import { useEffect, useState } from "react";
import { Copy, Plus, Trash2, UserPlus, FileText, Check, X, Mail, Send } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";
import { Field } from "./CockpitDossiers";

export function InvitesPanel({ dossierId }) {
    const [invites, setInvites] = useState([]);
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState("");
    const [resending, setResending] = useState("");
    const [resentMsg, setResentMsg] = useState("");

    const load = () =>
        cockpit
            .get(`/dossiers/${dossierId}/invites`)
            .then(({ data }) => setInvites(data));

    useEffect(() => {
        load();
    }, [dossierId]);

    const revoke = async (id) => {
        await cockpit.delete(`/invites/${id}`);
        load();
    };

    const resend = async (id) => {
        setResending(id);
        setResentMsg("");
        try {
            const { data } = await cockpit.post(`/invites/${id}/resend`);
            setResentMsg(
                data.ok
                    ? "Email réacheminé avec succès."
                    : `Erreur d'envoi : ${data.email_error || "service indisponible"}`
            );
            await load();
        } catch (e) {
            setResentMsg(
                e?.response?.data?.detail || "Erreur lors de la relance"
            );
        } finally {
            setResending("");
            setTimeout(() => setResentMsg(""), 4500);
        }
    };

    const fullUrl = (rel) =>
        rel.startsWith("http") ? rel : window.location.origin + rel;

    return (
        <div data-testid="invites-panel" className="mt-16">
            <div className="border-t border-white/[0.06] pt-12">
                <div className="flex items-end justify-between mb-8 gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-3">
                            <UserPlus strokeWidth={1.2} size={14} />
                            Accès client · invitations
                        </div>
                        <h2
                            className="font-serif text-[24px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            Espace client confidentiel
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="open-invite-modal"
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Plus strokeWidth={1.2} size={12} />
                        Nouvelle invitation
                    </button>
                </div>

                <div className="border-t border-white/[0.06]">
                    {resentMsg && (
                        <div
                            data-testid="invite-resent-msg"
                            className="mb-4 mt-4 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                        >
                            {resentMsg}
                        </div>
                    )}
                    {invites.length === 0 ? (
                        <p className="py-6 text-[13px] text-[#f4ecd8]/40 italic">
                            Aucun client n'a encore accès à ce dossier.
                        </p>
                    ) : (
                        invites.map((inv) => {
                            const status = inv.revoked
                                ? "Révoquée"
                                : inv.used_at
                                  ? "Activée"
                                  : "En attente";
                            const url = fullUrl(inv.invite_url);
                            const mailStatus = inv.email_sent
                                ? "Email remis"
                                : inv.email_error
                                  ? "Email non remis"
                                  : "Email non envoyé";
                            return (
                                <div
                                    key={inv.id}
                                    data-testid={`invite-${inv.id}`}
                                    className="grid lg:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 items-center py-5 border-b border-white/[0.06] px-2"
                                >
                                    <div className="min-w-0">
                                        <div
                                            className="font-serif text-[16px] text-[#f4ecd8] truncate"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {inv.client_name || inv.client_email}
                                        </div>
                                        <div className="text-[11px] text-[#f4ecd8]/45 mt-1 truncate flex items-center gap-2">
                                            <span>{inv.client_email}</span>
                                            <span
                                                title={
                                                    inv.email_error ||
                                                    (inv.email_sent
                                                        ? "Acheminé par Resend"
                                                        : "")
                                                }
                                                className={`inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase ${
                                                    inv.email_sent
                                                        ? "text-[#d4af37]/70"
                                                        : "text-[#e89090]/65"
                                                }`}
                                            >
                                                <Mail strokeWidth={1.3} size={10} />
                                                {mailStatus}
                                            </span>
                                        </div>
                                    </div>
                                    <span
                                        className={`text-[10px] tracking-[0.26em] uppercase ${
                                            inv.revoked
                                                ? "text-[#e89090]/70"
                                                : inv.used_at
                                                  ? "text-[#d4af37]"
                                                  : "text-[#f4ecd8]/55"
                                        }`}
                                    >
                                        {status}
                                    </span>
                                    <button
                                        type="button"
                                        data-testid={`copy-invite-${inv.id}`}
                                        onClick={() => {
                                            navigator.clipboard.writeText(url);
                                            setCopied(inv.id);
                                            setTimeout(() => setCopied(""), 1800);
                                        }}
                                        disabled={inv.revoked}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/70 hover:border-[#d4af37]/60 hover:text-[#d4af37] transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <Copy strokeWidth={1.2} size={11} />
                                        {copied === inv.id ? "Copié" : "Lien"}
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`resend-invite-${inv.id}`}
                                        onClick={() => resend(inv.id)}
                                        disabled={inv.revoked || resending === inv.id}
                                        title="Renvoyer l'email"
                                        className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.26em] uppercase border border-white/15 text-[#f4ecd8]/70 hover:border-[#d4af37]/60 hover:text-[#d4af37] transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <Send strokeWidth={1.2} size={11} />
                                        {resending === inv.id ? "Envoi…" : "Email"}
                                    </button>
                                    {!inv.revoked && (
                                        <button
                                            type="button"
                                            onClick={() => revoke(inv.id)}
                                            className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors duration-500 justify-self-end"
                                            title="Révoquer"
                                        >
                                            <Trash2 strokeWidth={1.2} size={14} />
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {open && (
                <InviteModal
                    dossierId={dossierId}
                    onClose={() => setOpen(false)}
                    onCreated={() => {
                        setOpen(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function InviteModal({ dossierId, onClose, onCreated }) {
    const [form, setForm] = useState({
        client_email: "",
        client_name: "",
        note: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.post(`/dossiers/${dossierId}/invites`, form);
            onCreated();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(
                Array.isArray(d)
                    ? "Adresse email invalide."
                    : d || "Erreur d'invitation"
            );
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            data-testid="invite-modal"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[520px] p-10 lg:p-14 mt-12 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="overline text-[#d4af37]/80 mb-5">Nouvelle invitation</div>
                <h2
                    className="font-serif text-[26px] text-[#f4ecd8] mb-10"
                    style={{ fontWeight: 300 }}
                >
                    Délivrer un accès client.
                </h2>
                <div className="space-y-6">
                    <Field
                        label="Nom du client"
                        value={form.client_name}
                        onChange={(e) =>
                            setForm({ ...form, client_name: e.target.value })
                        }
                        testid="invite-client-name"
                    />
                    <Field
                        label="Email du client"
                        type="email"
                        required
                        value={form.client_email}
                        onChange={(e) =>
                            setForm({ ...form, client_email: e.target.value })
                        }
                        testid="invite-client-email"
                    />
                    <Field
                        label="Mot personnel inclus dans l'email (facultatif)"
                        value={form.note}
                        onChange={(e) =>
                            setForm({ ...form, note: e.target.value })
                        }
                    />
                </div>
                <p className="mt-6 text-[11px] text-[#f4ecd8]/45 italic leading-relaxed">
                    Un email premium signé Joel Kotho sera adressé au client
                    avec son lien d'activation confidentiel (valable 14 jours).
                </p>
                {err && (
                    <div className="mt-6 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                        {err}
                    </div>
                )}
                <div className="mt-10 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        data-testid="invite-submit"
                        disabled={busy}
                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {busy ? "Création…" : "Générer le lien"}
                    </button>
                </div>
            </form>
        </div>
    );
}

// --------------- REPORTS ---------------
export function ReportsPanel({ dossierId }) {
    const [reports, setReports] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () =>
        cockpit
            .get(`/dossiers/${dossierId}/reports`)
            .then(({ data }) => setReports(data));

    useEffect(() => {
        load();
    }, [dossierId]);

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce compte rendu ?")) return;
        await cockpit.delete(`/reports/${id}`);
        load();
    };
    const validate = async (r) => {
        await cockpit.put(`/reports/${r.id}`, {
            title: r.title,
            body: r.body,
            validated: !r.validated,
        });
        load();
    };

    return (
        <div data-testid="reports-panel" className="mt-16">
            <div className="border-t border-white/[0.06] pt-12">
                <div className="flex items-end justify-between mb-8 gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-3">
                            <FileText strokeWidth={1.2} size={14} />
                            Comptes rendus partagés client
                        </div>
                        <h2
                            className="font-serif text-[24px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            Synthèses validées
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="open-report-modal"
                        onClick={() =>
                            setEditing({ title: "", body: "", validated: false })
                        }
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        <Plus strokeWidth={1.2} size={12} />
                        Rédiger un compte rendu
                    </button>
                </div>
                <div className="border-t border-white/[0.06]">
                    {reports.length === 0 ? (
                        <p className="py-6 text-[13px] text-[#f4ecd8]/40 italic">
                            Aucun compte rendu rédigé pour ce dossier.
                        </p>
                    ) : (
                        reports.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`report-${r.id}`}
                                className="grid lg:grid-cols-[1fr_auto_auto_auto] gap-4 items-center py-5 border-b border-white/[0.06] px-2"
                            >
                                <button
                                    type="button"
                                    onClick={() => setEditing(r)}
                                    className="text-left min-w-0"
                                >
                                    <div
                                        className="font-serif text-[16px] text-[#f4ecd8] truncate"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {r.title}
                                    </div>
                                    <div className="text-[11px] text-[#f4ecd8]/45 mt-1">
                                        {new Date(r.created_at).toLocaleString("fr-FR")}
                                    </div>
                                </button>
                                <span
                                    className={`text-[10px] tracking-[0.26em] uppercase ${
                                        r.validated
                                            ? "text-[#d4af37]"
                                            : "text-[#f4ecd8]/45"
                                    }`}
                                >
                                    {r.validated ? "Validé · client" : "Brouillon"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => validate(r)}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.26em] uppercase border border-white/15 hover:border-[#d4af37]/60 hover:text-[#d4af37] text-[#f4ecd8]/70 transition-all duration-500"
                                >
                                    <Check strokeWidth={1.4} size={11} />
                                    {r.validated ? "Dévalider" : "Valider"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(r.id)}
                                    className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors duration-500 justify-self-end"
                                >
                                    <Trash2 strokeWidth={1.2} size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
            {editing && (
                <ReportModal
                    dossierId={dossierId}
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function ReportModal({ dossierId, initial, onClose, onSaved }) {
    const isNew = !initial.id;
    const [form, setForm] = useState({
        title: initial.title || "",
        body: initial.body || "",
        validated: !!initial.validated,
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (isNew) {
                await cockpit.post(`/dossiers/${dossierId}/reports`, form);
            } else {
                await cockpit.put(`/reports/${initial.id}`, form);
            }
            onSaved();
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            data-testid="report-modal"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[640px] p-10 lg:p-14 mt-8 mb-20 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="overline text-[#d4af37]/80 mb-5">
                    {isNew ? "Nouveau compte rendu" : "Compte rendu"}
                </div>
                <h2
                    className="font-serif text-[26px] text-[#f4ecd8] mb-10"
                    style={{ fontWeight: 300 }}
                >
                    Synthèse partagée client.
                </h2>
                <div className="space-y-6">
                    <Field
                        label="Titre"
                        required
                        value={form.title}
                        onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                        }
                        testid="report-title"
                    />
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                            Contenu
                        </label>
                        <textarea
                            rows={8}
                            value={form.body}
                            data-testid="report-body"
                            onChange={(e) =>
                                setForm({ ...form, body: e.target.value })
                            }
                            required
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                        />
                    </div>
                    <label className="flex items-center gap-3 text-[12px] tracking-wide text-[#f4ecd8]/75 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.validated}
                            onChange={(e) =>
                                setForm({ ...form, validated: e.target.checked })
                            }
                            className="accent-[#d4af37]"
                        />
                        Marquer comme validé · visible côté client
                    </label>
                </div>
                <div className="mt-10 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        data-testid="report-save"
                        disabled={busy}
                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {busy ? "Enregistrement…" : isNew ? "Créer" : "Mettre à jour"}
                    </button>
                </div>
            </form>
        </div>
    );
}
